import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  PAGE_SIZE,
  fmtMoney,
  fmtDate,
  dateMatchLabel,
  rangeLabel,
  ScoreBadge,
  LoadingBlock,
  EmptyBlock,
  ErrorBlock,
  TableShell,
} from "./matching-ui";

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  const up = String(s).toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

const getRefOverlapTokens = (a?: string | null, b?: string | null): string[] => {
  const A = extractRefTokens(a);
  const B = extractRefTokens(b);
  if (!A.length || !B.length) return [];

  const setB = new Set(B);
  return A.filter((t) => setB.has(t));
};

function HighlightedReference({
  value,
  matchedTokens,
}: {
  value?: string | null;
  matchedTokens: string[];
}) {
  if (!value) return <span className="text-slate-400">—</span>;

  if (!matchedTokens.length) {
    return <span className="break-all">{value}</span>;
  }

  const uniqueTokens = Array.from(new Set(matchedTokens)).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${uniqueTokens.map(escapeRegExp).join("|")})`, "gi");
  const parts = value.split(pattern);

  return (
    <span className="break-all leading-6">
      {parts.map((part, idx) => {
        const isMatch = uniqueTokens.some((t) => t.toUpperCase() === part.toUpperCase());

        if (!isMatch) {
          return <React.Fragment key={idx}>{part}</React.Fragment>;
        }

        return (
          <span
            key={idx}
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 mx-[1px]",
              "bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200",
              "font-bold"
            )}
          >
            {part}
          </span>
        );
      })}
    </span>
  );
}

function PairHeader({
  leftTitle,
  rightTitle,
  leftLabel = "Paid expense",
  rightLabel = "Suggested bank matches",
  leftBadge,
  rightBadge,
}: {
  leftTitle: React.ReactNode;
  rightTitle: React.ReactNode;
  leftLabel?: string;
  rightLabel?: string;
  leftBadge?: React.ReactNode;
  rightBadge?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="px-3 py-2 bg-slate-50/70">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {leftLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {leftTitle}
              </div>
            </div>
            {leftBadge ? <div className="shrink-0">{leftBadge}</div> : null}
          </div>
        </div>

        <div className="px-3 py-2 border-t sm:border-t-0 sm:border-l border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-wide text-slate-500">
                {rightLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {rightTitle}
              </div>
            </div>
            {rightBadge ? <div className="shrink-0">{rightBadge}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  left,
  right,
  match,
}: {
  label: string;
  left: React.ReactNode;
  right: React.ReactNode;
  match?: React.ReactNode;
}) {
  return (
    <tr className="align-top">
      <td className="px-3 py-2 text-[10px] font-black tracking-wide text-slate-500 w-[120px]">
        {label}
      </td>
      <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
        {left}
      </td>
      <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
        {right}
      </td>
      <td className="px-3 py-2 text-center w-[110px]">
        {match ?? <span className="text-slate-300 text-[12px] font-semibold">—</span>}
      </td>
    </tr>
  );
}

function ExpenseDetailsTable({
  expense,
  candidate,
}: {
  expense: any;
  candidate?: any | null;
}) {
  const paidDate = expense.payment_date ?? expense.expense_date;
  const expAmount = expense.total_amount ?? expense.expense_total;
  const expCurrency = expense.currency ?? "";

  const expRef = expense.payment_reference ?? "";
  const txRef = candidate?.transaction_id_reference ?? "";
  const overlapTokens = candidate ? getRefOverlapTokens(expRef, txRef) : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full table-fixed">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide w-[120px]">
              Field
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Expense
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-wide">
              Bank
            </th>
            <th className="px-3 py-2 text-center text-[10px] font-black text-slate-500 tracking-wide w-[110px]">
              Match
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          <FieldRow
            label="Amount"
            left={
              <span className="tabular-nums">
                {expCurrency ? `${expCurrency} ${fmtMoney(expAmount)}` : fmtMoney(expAmount)}
              </span>
            }
            right={
              candidate ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular-nums">{fmtMoney(candidate.transaction_amount)}</span>
                  {candidate.transaction_fee != null ? (
                    <span className="text-[11px] text-slate-500">
                      fee {fmtMoney(candidate.transaction_fee)}
                    </span>
                  ) : null}
                </div>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            match={candidate ? <ScoreBadge score={candidate.score} /> : undefined}
          />

          <FieldRow
            label="Net"
            left={
              expense.net_payment != null ? (
                <span className="tabular-nums">{fmtMoney(expense.net_payment)}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={<span className="text-slate-400">—</span>}
          />

          <FieldRow
            label="Date"
            left={fmtDate(paidDate)}
            right={
              candidate ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>{fmtDate(candidate.transaction_date)}</span>
                  <Badge variant="outline" className="text-[11px] font-medium">
                    {dateMatchLabel(candidate.transaction_date, paidDate)}
                  </Badge>
                </div>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Reference ID"
            left={<HighlightedReference value={expRef} matchedTokens={overlapTokens} />}
            right={
              candidate ? (
                <HighlightedReference value={txRef} matchedTokens={overlapTokens} />
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />

          <FieldRow
            label="Expense #"
            left={
              expense.expense_number ? (
                <span className="font-mono text-[11px]">{expense.expense_number}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={<span className="text-slate-400">—</span>}
          />

          <FieldRow
            label="Category"
            left={
              expense.category?.name ? (
                <span>{expense.category.name}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={<span className="text-slate-400">—</span>}
          />

          <FieldRow
            label="Description"
            left={
              expense.description ? (
                <span className="truncate block max-w-[200px]" title={expense.description}>{expense.description}</span>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
            right={
              candidate ? (
                candidate.description_details ? (
                  <span className="truncate block max-w-[200px]" title={candidate.description_details}>{candidate.description_details}</span>
                ) : (
                  <span className="text-slate-400">—</span>
                )
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />
        </tbody>
      </table>
    </div>
  );
}

function CandidateCard({
  expense,
  candidate,
  idx,
}: {
  expense: any;
  candidate: any;
  idx: number;
}) {
  const whyText =
    Array.isArray(candidate.reasons) && candidate.reasons.length
      ? candidate.reasons.slice(0, 3).join(" · ")
      : "";

  return (
    <div
      className={cn(
        "rounded-lg border p-2.5 sm:p-3 shadow-sm",
        idx === 0 || candidate.score >= 95
          ? "border-emerald-200 bg-emerald-50/30"
          : "border-slate-200 bg-white"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-baseline gap-3">
          <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
            #{idx + 1}
          </div>

          {whyText ? (
            <div className="min-w-0 text-[11px] text-slate-700 truncate">
              <span className="font-black text-slate-900">Why:</span>{" "}
              <span className="font-semibold">{whyText}</span>
            </div>
          ) : null}
        </div>

        <div className="shrink-0">
          <ScoreBadge score={candidate.score} />
        </div>
      </div>

      <div className="mt-2">
        <PairHeader
          leftTitle={expense.vendor_name ?? "Vendor"}
          rightTitle={candidate.description_details ?? "Bank transaction"}
          leftLabel="Paid expense"
          rightLabel="Suggested bank match"
        />
      </div>

      <div className="mt-2">
        <ExpenseDetailsTable expense={expense} candidate={candidate} />
      </div>
    </div>
  );
}

function EmptyCandidateCard({ expense }: { expense: any }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm">
      <div className="mt-0">
        <PairHeader
          leftTitle={expense.vendor_name ?? "Vendor"}
          rightTitle="No matching bank transaction found"
          leftLabel="Paid expense"
          rightLabel="Suggested bank match"
        />
      </div>

      <div className="mt-2">
        <ExpenseDetailsTable expense={expense} candidate={null} />
      </div>

      <div className="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
        No matching bank transaction found in this date window.
      </div>
    </div>
  );
}

export function TabUnmatchedPaid({
  isLoading,
  error,
  unmatchedPaid,
  pageUnmatchedPaid,
  setPageUnmatchedPaid,
  totalUP,
}: {
  isLoading: boolean;
  error: unknown;
  unmatchedPaid: any[];
  pageUnmatchedPaid: number;
  setPageUnmatchedPaid: (p: number) => void;
  totalUP: number;
}) {
  const upItems = React.useMemo(
    () =>
      unmatchedPaid.slice(
        (pageUnmatchedPaid - 1) * PAGE_SIZE,
        pageUnmatchedPaid * PAGE_SIZE
      ),
    [unmatchedPaid, pageUnmatchedPaid]
  );

  const upRange = rangeLabel(pageUnmatchedPaid, unmatchedPaid.length);

  return (
    <TabsContent value="unmatched-paid" className="p-4 pt-4 space-y-2">
      <div className="text-[11px] text-slate-600">
        Paid expenses without a linked bank transaction. We show the most likely bank matches.
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <LoadingBlock />
        </div>
      ) : error ? (
        <ErrorBlock text="Failed to load matching suggestions." />
      ) : unmatchedPaid.length === 0 ? (
        <EmptyBlock text="No unmatched paid expenses in this range." />
      ) : (
        <TableShell
          page={pageUnmatchedPaid}
          setPage={setPageUnmatchedPaid}
          totalPages={totalUP}
          showing={upRange}
          total={unmatchedPaid.length}
          keyPrefix="up"
        >
          <div className="space-y-2">
            {upItems.map((expense: any, rowIdx: number) => {
              const globalRowNo = (pageUnmatchedPaid - 1) * PAGE_SIZE + rowIdx + 1;
              const hasCandidates =
                Array.isArray(expense.candidates) && expense.candidates.length > 0;

              return (
                <div
                  key={expense.id ?? expense.expense_id ?? rowIdx}
                  className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-baseline gap-3">
                      <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
                        #{globalRowNo}
                      </div>

                      <div className="min-w-0 text-[11px] text-slate-700 truncate">
                        <span className="font-black text-slate-900">Status:</span>{" "}
                        <span className="font-semibold">Unmatched paid expense</span>
                      </div>
                    </div>

                    {hasCandidates ? (
                      <div className="shrink-0">
                        <ScoreBadge score={expense.best_score ?? 0} />
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-2">
                    {hasCandidates ? (
                      <div className="space-y-2">
                        {expense.candidates.map((candidate: any, idx: number) => (
                          <CandidateCard
                            key={candidate.bank_transaction_id ?? idx}
                            expense={expense}
                            candidate={candidate}
                            idx={idx}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyCandidateCard expense={expense} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TableShell>
      )}
    </TabsContent>
  );
}